import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Plus,
  MoreHorizontal,
  Trash2,
  Copy,
  Edit,
  CheckCircle,
  Clock,
  Star,
  StarOff,
  Rocket,
  AlertTriangle,
  ArrowRightLeft,
  Archive
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { getMenus, deleteMenu, duplicateMenu, setDefaultMenu, unsetDefaultMenu, publishMenu } from '../services/api';
import { useChannel } from '../context/ChannelContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import GettingStarted from '../components/GettingStarted';

export const Dashboard = () => {
  const navigate = useNavigate();
  const { selectedChannelId, selectedChannel, channels } = useChannel();
  const { isWorkspaceAdmin } = useWorkspace();
  const { isSuperAdmin } = useAuth();
  // Deleting menus is admin-only on the server
  const canDelete = isWorkspaceAdmin || isSuperAdmin;
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [menuToDelete, setMenuToDelete] = useState(null);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [menuToCopy, setMenuToCopy] = useState(null);
  const [copyTargetChannelId, setCopyTargetChannelId] = useState('');
  const [copying, setCopying] = useState(false);

  const fetchMenus = async () => {
    if (!selectedChannelId) {
      setMenus([]);
      setLoading(false);
      return;
    }
    
    try {
      const data = await getMenus(selectedChannelId);
      setMenus(data.menus || []);
    } catch (error) {
      toast.error('Failed to load menus');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchMenus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChannelId]);

  const handleDelete = async () => {
    if (!menuToDelete) return;
    
    try {
      await deleteMenu(menuToDelete);
      toast.success('Menu deleted');
      fetchMenus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete menu');
    } finally {
      setDeleteDialogOpen(false);
      setMenuToDelete(null);
    }
  };

  const handleDuplicate = async (menuId) => {
    try {
      await duplicateMenu(menuId);
      toast.success('Menu duplicated');
      fetchMenus();
    } catch (error) {
      toast.error('Failed to duplicate menu');
    }
  };

  const handleCopyToChannel = async () => {
    if (!menuToCopy || !copyTargetChannelId) return;
    setCopying(true);
    try {
      await duplicateMenu(menuToCopy.id, copyTargetChannelId);
      const targetName = channels.find(c => c.id === copyTargetChannelId)?.name || 'channel';
      toast.success(`Menu copied to ${targetName}`);
      setCopyDialogOpen(false);
      setMenuToCopy(null);
      setCopyTargetChannelId('');
      // Refresh if copying to current channel
      if (copyTargetChannelId === selectedChannelId) fetchMenus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to copy menu');
    } finally {
      setCopying(false);
    }
  };

  const handleSetDefault = async (menuId) => {
    const menuName = menus.find(m => m.id === menuId)?.name || 'Menu';
    try {
      await setDefaultMenu(menuId);
      toast.success(`"${menuName}" is now the default menu`);
      fetchMenus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set default menu');
    }
  };

  const handleUnsetDefault = async () => {
    if (!selectedChannelId) return;
    
    try {
      await unsetDefaultMenu(selectedChannelId);
      toast.success('Default menu removed');
      fetchMenus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove default menu');
    }
  };

  const handlePublish = async (menuId) => {
    try {
      await publishMenu(menuId);
      toast.success('Menu published to LINE');
      fetchMenus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish menu');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Find the default menu
  const defaultMenu = menus.find(m => m.isDefault);

  // No channel selected state
  if (!selectedChannelId && channels.length === 0) {
    return (
      <div className="p-4 sm:p-6" data-testid="dashboard">
        <GettingStarted menus={[]} />
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 rounded-full bg-[#18181B] flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8 text-yellow-500" />
          </div>
          <h3 className="text-lg font-semibold mb-2">No channels configured</h3>
          <p className="text-[#A1A1AA] text-sm mb-6 max-w-sm">
            Connect your first LINE channel to start building rich menus. Follow the Getting started steps above, or add a channel now.
          </p>
          <Button 
            className="btn-primary"
            onClick={() => navigate('/channels', { state: { openAddChannel: true } })}
          >
            Add Channel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6" data-testid="dashboard">
      <GettingStarted menus={menus} />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Menus</h1>
          <p className="text-[#A1A1AA] text-sm mt-1">
            {selectedChannel ? (
              <>Menus for <span className="text-[#06C755]">{selectedChannel.name}</span></>
            ) : (
              'Select a channel to view menus'
            )}
          </p>
        </div>
        <Button 
          className="btn-primary w-full sm:w-auto"
          onClick={() => navigate('/create')}
          disabled={!selectedChannelId}
          data-testid="create-menu-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Menu
        </Button>
      </div>

      {/* Default menu banner */}
      {defaultMenu && (
        <div className="mb-6 p-4 bg-[#6366F1]/10 border border-[#6366F1]/20 rounded-lg flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#6366F1]/20 flex items-center justify-center flex-shrink-0">
              <Star className="w-5 h-5 text-[#6366F1]" />
            </div>
            <div>
              <p className="text-sm font-medium">
                Current Default: <span className="text-[#6366F1]">{defaultMenu.name}</span>
              </p>
              <p className="text-xs text-[#A1A1AA]">
                This menu is shown to all users by default
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleUnsetDefault()}
            className="border-[#6366F1]/30 text-[#6366F1] hover:bg-[#6366F1]/10"
          >
            <StarOff className="w-4 h-4 mr-2" />
            Remove default
          </Button>
        </div>
      )}

      {/* Menu list */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-[#00E05E] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : !selectedChannelId ? (
        <div className="empty-state" data-testid="no-channel-selected">
          <div className="w-16 h-16 rounded-full bg-[#18181B] flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8 text-[#52525B]" />
          </div>
          <h3 className="text-lg font-semibold mb-2">No channel selected</h3>
          <p className="text-[#A1A1AA] text-sm">Select a channel from the sidebar to view its menus</p>
        </div>
      ) : menus.length === 0 ? (
        <div className="empty-state" data-testid="empty-state">
          <div className="w-16 h-16 rounded-full bg-[#18181B] flex items-center justify-center mb-4">
            <Plus className="w-8 h-8 text-[#52525B]" />
          </div>
          <h3 className="text-lg font-semibold mb-2">No menus yet</h3>
          <p className="text-[#A1A1AA] text-sm mb-6">Create your first rich menu for this channel</p>
          <Button 
            className="btn-primary"
            onClick={() => navigate('/create')}
          >
            Create Menu
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
          {menus.map((menu) => (
            <div 
              key={menu.id}
              className="bg-[#18181B] border border-[#27272A] rounded-lg overflow-hidden hover:border-[#3F3F46] transition-colors"
              data-testid={`menu-card-${menu.id}`}
            >
              {/* Preview image */}
              <div 
                className="aspect-[2500/1686] bg-[#27272A] relative cursor-pointer"
                onClick={() => navigate(`/edit/${menu.id}`)}
              >
                {menu.imageUrl ? (
                  <img
                    src={menu.imageUrl}
                    alt={menu.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-[#52525B] text-sm">No image</span>
                  </div>
                )}
                
                {/* Status badge */}
                <div className="absolute top-2 left-2">
                  <span className={`status-badge ${menu.status === 'published' ? 'status-published' : 'status-draft'}${menu.status === 'replaced' ? ' opacity-70' : ''}`}>
                    {menu.status === 'replaced' ? (
                      <span className="flex items-center gap-1" title="A campaign launch replaced this menu. Duplicate it to reuse it.">
                        <Archive className="w-3 h-3" />
                        Replaced
                      </span>
                    ) : menu.status === 'published' ? (
                      <span className="flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Published
                      </span>
                    ) : menu.previousLineRichMenuId ? (
                      <span className="flex items-center gap-1" title="The previous version is still live on LINE. Publish to update it.">
                        <Clock className="w-3 h-3" />
                        Edited · republish
                      </span>
                    ) : (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Draft
                      </span>
                    )}
                  </span>
                </div>

                {/* Default badge */}
                {menu.isDefault && (
                  <div className="absolute top-2 right-2">
                    <span className="px-2 py-0.5 rounded bg-[#6366F1]/20 text-[#6366F1] text-xs font-medium flex items-center gap-1">
                      <Star className="w-3 h-3" />
                      Default
                    </span>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-3 sm:p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 
                      className="font-semibold truncate cursor-pointer hover:text-[#00E05E] text-sm sm:text-base"
                      onClick={() => navigate(`/edit/${menu.id}`)}
                    >
                      {menu.name || 'Untitled Menu'}
                    </h3>
                    <p className="text-xs text-[#52525B] mt-1">
                      {menu.areas?.length || 0} areas • {formatDate(menu.updatedAt)}
                    </p>
                    {menu.campaignId && (
                      <Link
                        to={`/campaigns/${menu.campaignId}`}
                        className="inline-block mt-1 px-1.5 py-0.5 rounded bg-[#27272A] text-[#A1A1AA] hover:text-white text-[10px] font-medium"
                        data-testid="menu-campaign-chip"
                      >
                        Campaign
                      </Link>
                    )}
                    {menu.richMenuAliasId && (
                      <p className="text-xs text-[#6366F1] mt-1 font-mono truncate">
                        {menu.richMenuAliasId}
                      </p>
                    )}
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="h-8 w-8 flex-shrink-0"
                        data-testid={`menu-actions-${menu.id}`}
                      >
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[#18181B] border-[#27272A]">
                      {menu.status !== 'replaced' && (
                        <DropdownMenuItem 
                          onClick={() => navigate(`/edit/${menu.id}`)}
                          className="focus:bg-[#27272A]"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => handleDuplicate(menu.id)}
                        className="focus:bg-[#27272A]"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Duplicate (same channel)
                      </DropdownMenuItem>
                      {channels.filter(c => c.id !== menu.channelId).length > 0 && (
                        <DropdownMenuItem
                          onClick={() => {
                            setMenuToCopy(menu);
                            setCopyTargetChannelId('');
                            setCopyDialogOpen(true);
                          }}
                          className="focus:bg-[#27272A]"
                        >
                          <ArrowRightLeft className="w-4 h-4 mr-2" />
                          Copy to Channel
                        </DropdownMenuItem>
                      )}
                      {menu.status !== 'published' && menu.status !== 'replaced' && (
                        <DropdownMenuItem 
                          onClick={() => handlePublish(menu.id)}
                          className="focus:bg-[#27272A] text-[#06C755]"
                        >
                          <Rocket className="w-4 h-4 mr-2" />
                          Publish
                        </DropdownMenuItem>
                      )}
                      {((menu.status === 'published' && !menu.isDefault) || menu.isDefault) && (
                        <DropdownMenuSeparator className="bg-[#27272A]" />
                      )}
                      {menu.status === 'published' && !menu.isDefault && (
                        <DropdownMenuItem 
                          onClick={() => handleSetDefault(menu.id)}
                          className="focus:bg-[#27272A] text-[#6366F1]"
                        >
                          <Star className="w-4 h-4 mr-2" />
                          Set as default
                        </DropdownMenuItem>
                      )}
                      {menu.isDefault && (
                        <DropdownMenuItem 
                          onClick={() => handleUnsetDefault()}
                          className="focus:bg-[#27272A] text-yellow-500"
                        >
                          <StarOff className="w-4 h-4 mr-2" />
                          Remove default
                        </DropdownMenuItem>
                      )}
                      {canDelete && (
                        <>
                          <DropdownMenuSeparator className="bg-[#27272A]" />
                          <DropdownMenuItem 
                            onClick={() => {
                              setMenuToDelete(menu.id);
                              setDeleteDialogOpen(true);
                            }}
                            className="focus:bg-[#27272A] text-red-400"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Copy to Channel dialog */}
      <Dialog open={copyDialogOpen} onOpenChange={setCopyDialogOpen}>
        <DialogContent className="bg-[#18181B] border-[#27272A] max-w-[90vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Copy to Channel</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Copy <span className="text-white font-medium">"{menuToCopy?.name}"</span> to another channel as a draft. The image and areas will be preserved; alias and publish status will be reset.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs text-[#A1A1AA] mb-2 block">Target Channel</Label>
            <Select value={copyTargetChannelId} onValueChange={setCopyTargetChannelId}>
              <SelectTrigger className="bg-[#09090B] border-[#27272A]">
                <SelectValue placeholder="Select a channel…" />
              </SelectTrigger>
              <SelectContent className="bg-[#18181B] border-[#27272A]">
                {channels
                  .filter(c => c.id !== menuToCopy?.channelId)
                  .map(c => (
                    <SelectItem key={c.id} value={c.id} className="focus:bg-[#27272A]">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${c.isValid ? 'bg-[#06C755]' : 'bg-yellow-500'}`} />
                        {c.name}
                      </div>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              className="border-[#27272A]"
              onClick={() => setCopyDialogOpen(false)}
              disabled={copying}
            >
              Cancel
            </Button>
            <Button
              className="btn-primary"
              onClick={handleCopyToChannel}
              disabled={!copyTargetChannelId || copying}
            >
              {copying ? 'Copying…' : 'Copy Menu'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#18181B] border-[#27272A] max-w-[90vw] sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Menu</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              Are you sure you want to delete this menu? This action cannot be undone.
              If the menu is published, it will also be removed from LINE.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="bg-[#27272A] border-[#3F3F46] hover:bg-[#3F3F46] w-full sm:w-auto">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Dashboard;
